import { useState } from "react";

import { CombinedQuestionState } from "./types";

/**
 * Local state hook for question components without QuestionProvider.
 *
 * Supports provider-less component usage, useful for:
 * - Standalone usage outside the main application
 * - Storybook stories without Redux setup
 * - Unit testing components in isolation
 *
 * Decouples question components from QuestionProvider, ensuring they
 * work in all scenarios whether wrapped in a provider or not.
 */
export function useLocalQuestionState(): CombinedQuestionState {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>();
  const [submittedOptionIndexes, setSubmittedOptionIndexes] = useState<
    number[]
  >([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
  const [submittedOptionIds, setSubmittedOptionIds] = useState<number[]>([]);
  const [attemptsCount, setAttemptsCount] = useState(0);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [value, setValueState] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [textChanged, setTextChanged] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState<string[]>([]);
  const [isSubmittedAnatomy, setIsSubmittedAnatomy] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  return {
    selectedOptionIndex,
    submittedOptionIndexes,
    selectedOptionIds,
    submittedOptionIds,
    attemptsCount,
    hasAttempted,
    value,
    isCompleted,
    textChanged,
    answers,
    correctAnswers,
    incorrectAnswers,
    isSubmitted: isSubmittedAnatomy,
    showFeedback,
    selectOption: (idx: number) => setSelectedOptionIndex(idx),
    toggleOption: (id: number) =>
      setSelectedOptionIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      ),
    setValue: (v: string) => {
      setValueState(v);
      setTextChanged(true);
    },
    setAnswer: (calloutId: string, val: string) => {
      setAnswers((prev) => ({ ...prev, [calloutId]: val }));
      setIncorrectAnswers((prev) => prev.filter((id) => id !== calloutId));
      setShowFeedback(false);
    },
    submitAnatomy: (callouts: { id: string; correctAnswer: string }[]) => {
      const correct: string[] = [];
      const incorrect: string[] = [];
      callouts.forEach((c) => {
        if (answers[c.id] === c.correctAnswer) {
          correct.push(c.id);
        } else {
          incorrect.push(c.id);
        }
      });
      setCorrectAnswers(correct);
      setIncorrectAnswers(incorrect);
      setIsSubmittedAnatomy(true);
      setShowFeedback(true);
    },
    tryAgain: () => {
      setSelectedOptionIds([]);
      setSubmittedOptionIds([]);
    },
    submit: () => {
      if (selectedOptionIndex !== undefined) {
        setSubmittedOptionIndexes((prev) => [...prev, selectedOptionIndex]);
      }
      setSubmittedOptionIds(selectedOptionIds);
      setAttemptsCount((prev) => prev + 1);
      setHasAttempted(true);
      setIsCompleted(true);
    },
    reset: () => {
      setSelectedOptionIndex(undefined);
      setSubmittedOptionIndexes([]);
      setSelectedOptionIds([]);
      setSubmittedOptionIds([]);
      setAttemptsCount(0);
      setHasAttempted(false);
      setValueState("");
      setIsCompleted(false);
      setTextChanged(false);
      setAnswers({});
      setCorrectAnswers([]);
      setIncorrectAnswers([]);
      setIsSubmittedAnatomy(false);
      setShowFeedback(false);
    },
  };
}
